/**
 * 本地存储工具（Vue3 web 版）
 * - 基于 localStorage
 * - 支持 token 设置 / 获取 / 移除
 * - 支持自动校验 token 是否过期
 */

const TOKEN_KEY = 'userToken'; // token
const TOKEN_EXPIRE_KEY = 'userTokenExpire'; // token 过期时间（时间戳，单位 ms）
const TOKEN_TIMESTAMP_KEY = 'userTokenTimestamp'; // token 设置时间戳
const USER_INFO_KEY = 'userInfo'; // 本地用户信息

/**
 * 安全读取 localStorage
 */
const safeGet = (key) => {
	try {
		return localStorage.getItem(key);
	} catch (error) {
		console.error(`读取 ${key} 失败:`, error);
		return null;
	}
};

/**
 * 安全写入 localStorage（自动 JSON 序列化对象）
 */
const safeSet = (key, value) => {
	try {
		const val = typeof value === 'string' ? value : JSON.stringify(value);
		localStorage.setItem(key, val);
		return true;
	} catch (error) {
		console.error(`写入 ${key} 失败:`, error);
		return false;
	}
};

/**
 * 安全移除 localStorage
 */
const safeRemove = (key) => {
	try {
		localStorage.removeItem(key);
		return true;
	} catch (error) {
		console.error(`移除 ${key} 失败:`, error);
		return false;
	}
};

/**
 * 获取 token（不校验过期）
 */
export const getToken = () => {
	return safeGet(TOKEN_KEY);
};

/**
 * 获取 token 过期时间戳（ms）
 */
export const getTokenExpire = () => {
	const expire = safeGet(TOKEN_EXPIRE_KEY);
	return expire ? Number(expire) : null;
};

/**
 * 获取 token 设置时的时间戳（ms）
 */
export const getTokenTimeStamp = () => {
	const ts = safeGet(TOKEN_TIMESTAMP_KEY);
	return ts ? Number(ts) : null;
};

/**
 * 设置 token
 * @param {string} token 登录令牌
 * @param {number} expiresIn 有效期（秒），默认 7200 = 2 小时
 */
export const setToken = (token, expiresIn = 7200) => {
	const now = Date.now();
	const expireTime = now + expiresIn * 1000;
	const ok =
		safeSet(TOKEN_KEY, token) &&
		safeSet(TOKEN_EXPIRE_KEY, String(expireTime)) &&
		safeSet(TOKEN_TIMESTAMP_KEY, String(now));
	return ok;
};

/**
 * 移除 token（含过期时间、时间戳）
 */
export const removeToken = () => {
	const ok =
		safeRemove(TOKEN_KEY) &&
		safeRemove(TOKEN_EXPIRE_KEY) &&
		safeRemove(TOKEN_TIMESTAMP_KEY);
	return ok;
};

/**
 * 校验 token 是否有效
 * - 不存在 → false
 * - 已过期 → 自动清除并返回 false
 * - 未过期 → true
 */
export const isTokenValid = () => {
	const token = getToken();
	if (!token) return false;

	const expire = getTokenExpire();
	if (!expire) return false; // 无过期时间，视为无效

	if (Date.now() >= expire) {
		// 已过期，自动清理
		removeToken();
		return false;
	}
	return true;
};

/**
 * 获取 token 剩余有效时长（秒），已过期或无 token 返回 0
 */
export const getTokenRemaining = () => {
	const expire = getTokenExpire();
	if (!expire) return 0;
	const remainMs = expire - Date.now();
	return remainMs > 0 ? Math.floor(remainMs / 1000) : 0;
};

/**
 * 获取本地存储的用户信息（自动反序列化）
 */
export const getLocalUserInfo = () => {
	const raw = safeGet(USER_INFO_KEY);
	if (!raw) return null;
	try {
		// 字符串直接返回；JSON 字符串反序列化
		if (raw.startsWith('{') || raw.startsWith('[')) {
			return JSON.parse(raw);
		}
		return raw;
	} catch (error) {
		console.error('解析本地用户信息失败:', error);
		return raw;
	}
};

/**
 * 设置本地存储的用户信息（自动序列化对象）
 */
export const setLocalUserInfo = (userInfo) => {
	return safeSet(USER_INFO_KEY, userInfo);
};

/**
 * 移除本地存储的用户信息
 */
export const removeLocalUserInfo = () => {
	return safeRemove(USER_INFO_KEY);
};

/**
 * 清除所有本地用户信息（token + 用户信息）
 */
export const clearLocalUser = () => {
	removeToken();
	removeLocalUserInfo();
};

export default {
	getToken,
	getTokenExpire,
	getTokenTimeStamp,
	setToken,
	removeToken,
	isTokenValid,
	getTokenRemaining,
	getLocalUserInfo,
	setLocalUserInfo,
	removeLocalUserInfo,
	clearLocalUser,
};
